import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
  type MotionValue,
} from 'framer-motion';
import { useRef, useState } from 'react';
import { DUR, EASE_OUT_QUINT } from '@/lib/motion';

/**
 * Trace — Figure 2.
 *
 * Pinned-scroll algorithm theatre. As the user scrolls, the section is
 * sticky at the top of the viewport. A horizontal byte ribbon shows a
 * real input string being encoded by LZSS, step by step:
 *
 *   1. Intro          — the input arrives
 *   2. Literals       — cursor moves through unseen bytes, each emitted as-is
 *   3. Window         — a sliding window appears behind the cursor
 *   4. Match          — cursor reaches a repeat; scan-back animates
 *   5. Back-reference — emit (offset, length) instead of bytes
 *
 * Body copy on the left swaps in sync with the visual on the right.
 */

const INPUT = 'the cat sat the cat sat';
const MATCH_AT = 12;
const MATCH_LEN = 11;
const MATCH_BACK = 12;

const STEPS = [
  {
    label: '01 · input',
    title: 'Watch LZSS encode a string.',
    body: 'Twenty-three bytes go in. The encoder reads one at a time, left to right, and decides: literal, or back-reference?',
  },
  {
    label: '02 · literals',
    title: 'First, every byte is new.',
    body: 'Nothing has been seen yet. The encoder has no choice; each byte is written to the output as a literal.',
  },
  {
    label: '03 · window',
    title: 'A window remembers what passed.',
    body: 'Behind the cursor sits a sliding window, up to 4096 bytes of recent input. Every byte already encoded stays in there for a while.',
  },
  {
    label: '04 · match',
    title: 'Then a familiar shape appears.',
    body: 'The next eleven bytes ("the cat sat") already exist twelve bytes back in the window. The encoder spots the repeat.',
  },
  {
    label: '05 · pointer',
    title: 'Write a pointer, not the bytes.',
    body: 'Instead of eleven literal bytes, the encoder emits one back-reference: (offset 12, length 11). Same data, fewer bits.',
  },
];

export function Trace() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  // 5 steps × 100vh = total scroll length. The sticky inner pins to top.

  // Per-step thresholds (each step covers ~0.2 of progress).
  const cursorPos = useTransform(
    scrollYProgress,
    [0, 0.18, 0.42, 0.6, 0.78, 1],
    [0, 0, MATCH_AT, MATCH_AT, MATCH_AT, INPUT.length],
  );
  const windowOpacity = useTransform(scrollYProgress, [0.36, 0.52], [0, 1]);
  const windowEnd = useTransform(
    scrollYProgress,
    [0, 0.18, 0.42, 1],
    [0, 0, MATCH_AT, MATCH_AT],
  );
  const matchOpacity = useTransform(scrollYProgress, [0.58, 0.72], [0, 1]);
  const pointerOpacity = useTransform(scrollYProgress, [0.78, 0.9], [0, 1]);

  const literalCount = useTransform(
    scrollYProgress,
    [0, 0.18, 0.42, 1],
    [0, 0, MATCH_AT, MATCH_AT],
  );
  const showPointer = useTransform(scrollYProgress, [0.85, 0.9], [0, 1]);

  // Discretized step index for the text column.
  // Reduced-motion users see the final step pinned (full result reveal),
  // skipping the scroll-driven progression entirely.
  const reduced = useReducedMotion() ?? false;
  const [stepIndex, setStepIndex] = useState(reduced ? STEPS.length - 1 : 0);
  useMotionValueEvent(scrollYProgress, 'change', (p) => {
    if (reduced) return;
    const idx = Math.min(STEPS.length - 1, Math.max(0, Math.floor(p * STEPS.length - 0.0001)));
    setStepIndex(idx);
  });

  const step = STEPS[stepIndex];

  return (
    <>
      <style>{`
        .trace-grid {
          display: grid;
          grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);
          gap: 56px;
          align-items: center;
        }
        @media (max-width: 900px) {
          .trace-grid {
            grid-template-columns: minmax(0, 1fr);
            gap: 40px;
          }
          /* On stacked layout the visual goes first so the user sees
           * the byte ribbon land into focus before reading the prose. */
          .trace-grid > .trace-visual { order: 0; }
          .trace-grid > .trace-text   { order: 1; }
        }
        @media (max-width: 720px) {
          /* Below tablet, the sticky pin gets in the way of native
           * momentum scroll. Drop the section to one viewport tall and
           * let the steps progress as a normal scroll list. */
          .trace-section { height: auto !important; }
          .trace-pinned  { position: relative !important; height: auto !important; padding: 80px 20px !important; }
          /* The 23-byte ribbon is wider than a phone viewport. Let the
           * visual scroll horizontally instead of overflowing the section. */
          .trace-visual { overflow-x: auto; -webkit-overflow-scrolling: touch; margin-inline: -20px; padding-inline: 20px; }
          .trace-visual > * { min-width: 640px; }
        }
      `}</style>
    <section
      ref={sectionRef}
      id="trace"
      className="trace-section"
      style={{
        background: 'var(--bg-warm)',
        position: 'relative',
        // NOTE: do NOT set `overflow: hidden` here. A non-visible overflow
        // on the section's containing block kills `position: sticky` on
        // .trace-pinned — that breaks the entire pinned-scroll animation.
        height: `${STEPS.length * 80}dvh`,
      }}
    >
      {/* Atmosphere — workbench ruler grid.
       *  Pinned to the viewport so a moving sticky child doesn't pull it
       *  along, with pointer-events: none and below the pinned content. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundImage:
            'repeating-linear-gradient(to bottom, transparent 0, transparent 47px, var(--border) 47px, var(--border) 48px)',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />
      <div
        className="trace-pinned"
        style={{
          position: 'sticky',
          top: 0,
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px',
          zIndex: 1,
        }}
      >
        <div
          className="container-x trace-grid"
          style={{
            width: '100%',
          }}
        >
          {/* ── Text column ─────────────────────────────────────────── */}
          <div className="trace-text" style={{ maxWidth: '36ch' }}>
            <span
              className="t-mono"
              style={{
                fontSize: 'var(--type-xs)',
                color: 'var(--accent)',
                letterSpacing: 'var(--tracking-mono-caps)',
                textTransform: 'uppercase',
              }}
            >
              figure 2
            </span>

            <motion.div
              key={stepIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DUR.base, ease: EASE_OUT_QUINT }}
              style={{ marginTop: 16 }}
            >
              <span
                className="t-mono"
                style={{
                  fontSize: 'var(--type-xs)',
                  color: 'var(--text-3)',
                  letterSpacing: 'var(--tracking-mono-caps)',
                  textTransform: 'uppercase',
                }}
              >
                {step.label}
              </span>
              <h2
                className="t-heading"
                style={{
                  marginTop: 12,
                  fontSize: 'var(--type-lg)',
                  letterSpacing: 'var(--tracking-tight)',
                  lineHeight: 1.15,
                }}
              >
                {step.title}
              </h2>
              <p
                style={{
                  marginTop: 16,
                  fontSize: 'var(--type-md)',
                  color: 'var(--text-2)',
                  lineHeight: 1.55,
                }}
              >
                {step.body}
              </p>
            </motion.div>

            {/* Step pips */}
            <div style={{ display: 'flex', gap: 8, marginTop: 32 }}>
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: i === stepIndex ? 24 : 8,
                    height: 2,
                    background: i <= stepIndex ? 'var(--accent)' : 'var(--border-strong)',
                    transition:
                      'width var(--dur-base) var(--ease-out-expo), background var(--dur-base) var(--ease-out-expo)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* ── Visual column ──────────────────────────────────────── */}
          <div className="trace-visual">
          <Visual
            cursorPos={cursorPos}
            windowOpacity={windowOpacity}
            windowEnd={windowEnd}
            matchOpacity={matchOpacity}
            pointerOpacity={pointerOpacity}
            literalCount={literalCount}
            showPointer={showPointer}
          />
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

/* ── Visual ────────────────────────────────────────────────────────── */

// Cells bumped to read clearly from a normal viewing distance. The
// 23-byte ribbon is `23 * (CELL_W + CELL_GAP) - CELL_GAP = 618px`.
// Visual column width at 1120px container with 4fr/8fr split + 56px
// gap is ~664px, panel inner ~636 — fits with margin. Below 900px
// the layout stacks (media query) and the visual gets full width.
const CELL_W = 24;
const CELL_GAP = 3;

function Visual({
  cursorPos,
  windowOpacity,
  windowEnd,
  matchOpacity,
  pointerOpacity,
  literalCount,
  showPointer,
}: {
  cursorPos: MotionValue<number>;
  windowOpacity: MotionValue<number>;
  windowEnd: MotionValue<number>;
  matchOpacity: MotionValue<number>;
  pointerOpacity: MotionValue<number>;
  literalCount: MotionValue<number>;
  showPointer: MotionValue<number>;
}) {
  // x-coord of a byte cell's left edge inside the ribbon
  const cellX = (i: number) => i * (CELL_W + CELL_GAP);

  const cursorX = useTransform(cursorPos, (p) => cellX(p));
  const windowX = useTransform(windowEnd, (p) => Math.max(0, cellX(p) - cellX(0)));
  const windowW = useTransform(windowEnd, (p) => Math.max(0, cellX(p)));

  const matchOriginX = cellX(0);
  const matchCursorX = cellX(MATCH_AT);
  const matchTopY = -22;

  return (
    <div
      style={{
        position: 'relative',
        padding: '36px 14px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 16,
      }}
    >
      {/* Top label strip */}
      <div
        className="t-mono"
        style={{
          fontSize: 'var(--type-xs)',
          color: 'var(--text-3)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>input · {INPUT.length} bytes</span>
        <span>cursor sweeps left → right</span>
      </div>

      {/* Byte ribbon — fixed width so positions are stable */}
      <div
        style={{
          position: 'relative',
          height: 96,
          width: cellX(INPUT.length) - CELL_GAP,
          minWidth: cellX(INPUT.length) - CELL_GAP,
        }}
      >
        {/* Sliding window backdrop */}
        <motion.div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: CELL_W,
            x: windowX,
            width: windowW,
            background: 'var(--accent-bg)',
            border: '1px dashed var(--accent-soft)',
            borderRadius: 6,
            opacity: windowOpacity,
            transformOrigin: 'left',
          }}
        />

        {/* Bytes */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'flex',
            gap: CELL_GAP,
          }}
        >
          {INPUT.split('').map((ch, i) => (
            <ByteChar key={i} ch={ch} index={i} literalCount={literalCount} />
          ))}
        </div>

        {/* Cursor — tall vertical line, thicker for emphasis */}
        <motion.div
          aria-hidden
          style={{
            position: 'absolute',
            top: -10,
            left: 0,
            x: cursorX,
            width: 3,
            height: CELL_W + 20,
            background: 'var(--accent)',
            boxShadow: '0 0 12px var(--accent-glow)',
            transformOrigin: 'top',
          }}
        />

        {/* Match arc — connects MATCH_AT back to position 0 */}
        <motion.svg
          aria-hidden
          style={{
            position: 'absolute',
            top: -42,
            left: 0,
            overflow: 'visible',
            opacity: matchOpacity,
          }}
          width={cellX(MATCH_AT) + CELL_W}
          height={42}
        >
          <path
            d={`M ${matchCursorX + CELL_W / 2} 42 C ${
              matchCursorX + CELL_W / 2
            } ${matchTopY}, ${matchOriginX + CELL_W / 2} ${matchTopY}, ${
              matchOriginX + CELL_W / 2
            } 42`}
            stroke="var(--accent)"
            strokeWidth={2.25}
            fill="none"
            strokeLinecap="round"
          />
          <circle cx={matchOriginX + CELL_W / 2} cy={42} r={4} fill="var(--accent)" />
          <circle cx={matchCursorX + CELL_W / 2} cy={42} r={4} fill="var(--accent)" />
        </motion.svg>
      </div>

      {/* Output stream */}
      <div style={{ marginTop: 32 }}>
        <div
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--text-3)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          output stream
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 36 }}>
          {INPUT.slice(0, MATCH_AT).split('').map((ch, i) => (
            <LiteralToken key={i} index={i} literalCount={literalCount} ch={ch} />
          ))}
          <PointerToken showPointer={showPointer} pointerOpacity={pointerOpacity} />
        </div>
      </div>
    </div>
  );
}

function ByteChar({
  ch,
  index,
  literalCount,
}: {
  ch: string;
  index: number;
  literalCount: MotionValue<number>;
}) {
  const opacity = useTransform(literalCount, (c) => {
    if (index < c) return 0.5; // already emitted
    if (index === Math.floor(c)) return 1; // current
    return 0.85; // upcoming
  });

  return (
    <motion.span
      style={{
        width: CELL_W,
        height: CELL_W,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 15,
        fontWeight: 600,
        color: 'var(--text)',
        background: 'var(--bg-warm)',
        border: '1px solid var(--border-strong)',
        borderRadius: 5,
        opacity,
      }}
    >
      {ch === ' ' ? '·' : ch}
    </motion.span>
  );
}

function LiteralToken({
  ch,
  index,
  literalCount,
}: {
  ch: string;
  index: number;
  literalCount: MotionValue<number>;
}) {
  const opacity = useTransform(literalCount, (c) => (index < c ? 1 : 0));
  const scale = useTransform(literalCount, (c) => (index < c ? 1 : 0.85));

  return (
    <motion.span
      style={{
        opacity,
        scale,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 28,
        padding: '0 8px',
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--text-2)',
        background: 'var(--bg-warm)',
        border: '1px solid var(--border-strong)',
        borderRadius: 5,
      }}
    >
      {ch === ' ' ? '·' : ch}
    </motion.span>
  );
}

function PointerToken({
  showPointer,
  pointerOpacity,
}: {
  showPointer: MotionValue<number>;
  pointerOpacity: MotionValue<number>;
}) {
  const scale = useTransform(showPointer, [0, 1], [0.85, 1]);
  return (
    <motion.span
      style={{
        opacity: pointerOpacity,
        scale,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 28,
        padding: '0 14px',
        fontFamily: 'var(--font-mono)',
        fontSize: 13,
        fontWeight: 700,
        color: 'var(--bg)',
        background: 'var(--accent)',
        borderRadius: 5,
        letterSpacing: 0.4,
        boxShadow: '0 4px 16px var(--accent-glow)',
      }}
    >
      &lt;{MATCH_BACK},{MATCH_LEN}&gt;
    </motion.span>
  );
}
